'use client';

/**
 * ThemeProvider — context wrapper that bundles `useTheme` + `useDensity`
 * into one subscription.
 *
 * Consumers read via `useThemeContext()` — safe to call from any Client
 * Component under `<ThemeProvider>`.
 *
 * The provider does NOT set the theme before first paint — that job is
 * owned by the inline FOUC script in `app/layout.tsx`.  All this does
 * is keep React state in sync with the `<html>` attribute, and let
 * children flip the preference at runtime.
 *
 * White-label overrides are applied separately — the authenticated
 * shell (`app/(app)/layout.tsx`) calls `applyWhiteLabel()` directly
 * in its own effect.  Keeping the two concerns separate means this
 * provider has one responsibility and one place to debug.
 */
import type { ReactNode } from 'react';
import { createContext, useContext } from 'react';

import type { Density, ResolvedTheme, ThemePreference } from './types';
import { useDensity } from './useDensity';
import { useTheme } from './useTheme';

interface ThemeContextValue {
  pref: ThemePreference;
  resolved: ResolvedTheme;
  setPref: (p: ThemePreference) => void;
  density: Density;
  setDensity: (d: Density) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { pref, resolved, setPref } = useTheme();
  const { density, setDensity } = useDensity();

  return (
    <ThemeContext.Provider value={{ pref, resolved, setPref, density, setDensity }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useThemeContext(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error(
      'useThemeContext() called outside <ThemeProvider>. Wrap your tree in (app)/layout.tsx.',
    );
  }
  return ctx;
}
